import { useCallback, useMemo } from "react";
import { FlatList } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import { View, Text } from "@src/components";
import type { ScenarioRow, ScenarioSiteGroup } from "../utils/scenario-group";
import { ScenarioCard } from "./ScenarioCard";

interface ScenarioListProps {
  groups: ScenarioSiteGroup[];
}

type ListItem =
  | { type: "header"; key: string; siteLabel: string }
  | { type: "row"; key: string; cells: (ScenarioRow | undefined)[] };

const NUM_COLUMNS = 2;

const keyExtractor = (item: ListItem) => item.key;

const buildRows = (rows: ScenarioRow[]): ListItem[] => {
  const result: ListItem[] = [];
  for (let i = 0; i < rows.length; i += NUM_COLUMNS) {
    const cells: (ScenarioRow | undefined)[] = [];
    for (let c = 0; c < NUM_COLUMNS; c++) {
      cells.push(rows[i + c]);
    }
    const firstCell = cells.find(Boolean);
    const rowKey = firstCell ? `row-${firstCell.key}` : `row-empty-${i}`;
    result.push({ type: "row", key: rowKey, cells });
  }
  return result;
};

export function ScenarioList({ groups }: ScenarioListProps) {
  const items = useMemo((): ListItem[] => {
    const showHeaders = groups.length > 1;
    return groups.flatMap((group) => {
      const totalScenarios = group.rows.length;
      const headerLabel = `${group.siteName} · ${totalScenarios} ${totalScenarios === 1 ? "scenario" : "scenarios"}`;
      const headerItem: ListItem[] =
        showHeaders ? [{ type: "header", key: `header-${group.siteId}`, siteLabel: headerLabel }] : [];
      return [...headerItem, ...buildRows(group.rows)];
    });
  }, [groups]);

  const renderItem = useCallback(({ item }: { item: ListItem }) => {
    if (item.type === "header") {
      return (
        <View style={styles.headerSlot}>
          <Text textStyle="xs/medium" style={styles.headerText}>
            {item.siteLabel}
          </Text>
        </View>
      );
    }

    return (
      <View style={styles.row}>
        {item.cells.map((cell, i) => (
          <View key={cell?.key ?? `empty-${item.key}-${i}`} style={styles.cell}>
            {cell ?
              <ScenarioCard
                scenarioName={cell.scenarioName}
                cameraDisplayName={cell.cameraDisplayName}
                cameraSubtitle={cell.cameraSubtitle}
              />
            : null}
          </View>
        ))}
      </View>
    );
  }, []);

  return (
    <FlatList
      data={items}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      contentContainerStyle={styles.listContentContainer}
      showsVerticalScrollIndicator={false}
      removeClippedSubviews={false}
      windowSize={5}
      maxToRenderPerBatch={8}
      initialNumToRender={8}
    />
  );
}

const styles = StyleSheet.create((theme) => ({
  listContentContainer: {
    flexGrow: 1,
    paddingHorizontal: theme.spacing["5xl"] - theme.spacing.md / 2,
    paddingBottom: theme.spacing["7xl"],
  },
  headerSlot: {
    paddingHorizontal: theme.spacing.md / 2,
    paddingTop: theme.spacing["4xl"],
    paddingBottom: theme.spacing.lg,
  },
  headerText: {
    color: theme.colors.interactive.controls.default_midground,
  },
  row: {
    flexDirection: "row",
    alignItems: "stretch",
  },
  cell: {
    flex: 1,
    paddingHorizontal: theme.spacing.md / 2,
    paddingBottom: theme.spacing.lg,
  },
}));
